import type { LucideIcon } from 'lucide-react';
import { ExternalLink } from 'lucide-react';

interface SiteCardProps {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
  gradient: string;
  glowColor: string;
  shadowColor: string;
  status: 'online' | 'coming-soon';
  index: number;
}

const animationClass = [
  'animate-fade-in-up-delay-1',
  'animate-fade-in-up-delay-2',
  'animate-fade-in-up-delay-3',
];

export function SiteCard({
  title,
  description,
  href,
  icon: Icon,
  gradient,
  glowColor,
  shadowColor,
  status,
  index,
}: SiteCardProps) {
  const isComingSoon = status === 'coming-soon';

  const Wrapper = isComingSoon ? 'div' : 'a';

  return (
    <Wrapper
      {...(!isComingSoon && { href, target: '_blank', rel: 'noopener noreferrer' })}
      className={`
        site-card group block rounded-2xl
        border border-white/[0.06] 
        bg-gradient-to-b from-slate-800/60 to-slate-900/80
        backdrop-blur-sm
        p-8 sm:p-10
        aspect-square flex flex-col
        ${isComingSoon ? 'opacity-60 cursor-default' : 'cursor-pointer hover:border-white/[0.12]'}
        ${animationClass[index] || 'animate-fade-in-up-delay-1'}
      `}
      style={{
        boxShadow: isComingSoon ? 'none' : `0 0 0 0 ${shadowColor}`,
        ...(isComingSoon ? {} : {
          '--hover-shadow': `0 8px 40px -8px ${shadowColor}`,
        } as React.CSSProperties),
      }}
      onMouseEnter={(e) => {
        if (!isComingSoon) {
          (e.currentTarget as HTMLElement).style.boxShadow =
            `0 8px 40px -8px ${shadowColor}`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isComingSoon) {
          (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        }
      }}
    >
      {/* Glow overlay */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${glowColor}, transparent 70%)` }}
      />

      {/* Icon */}
      <div className={`
        w-14 h-14 rounded-xl flex items-center justify-center mb-6
        bg-gradient-to-br ${gradient}
        shadow-lg
        ${isComingSoon ? '' : 'group-hover:scale-110 transition-transform duration-300'}
      `}>
        <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
      </div>

      {/* Title row */}
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-slate-100">
          {title}
        </h2>
        {isComingSoon ? (
          <span className="text-[11px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
            скоро
          </span>
        ) : (
          <ExternalLink className="w-4 h-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        )}
      </div>

      {/* Description */}
      <p className="text-slate-400 text-sm sm:text-base leading-relaxed font-light">
        {description}
      </p>

      {/* Status dot */}
      {!isComingSoon && (
        <div className="mt-6 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-slow" />
          <span className="text-xs text-slate-500">онлайн</span>
        </div>
      )}
    </Wrapper>
  );
}
